import { IGroupPaymentInfo, IOrder } from '@/interfaces/IOrder';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { OrderPaymentStatus, OrderStatus } from '@/enums/OrderEnums';
import { PrintSource, TicketPrintSetting } from '@/enums/PrintEnum';

/**
 * Pure decision layer for auto-printing. STATE-BASED (level predicates over
 * the order's current state), not edge-based: decisions can be safely
 * re-evaluated any number of times because idempotency comes from the
 * per-item printed counters (printT/printC/printE).
 */

/** Canceled/declined orders must never auto-print (live or catch-up). */
export function isOrderAutoPrintable(order: Pick<IOrder, 'status'>): boolean {
  return (
    order.status !== OrderStatus.Canceled &&
    order.status !== OrderStatus.Declined
  );
}

/**
 * Whether the configured trigger/source fires for the order's current state.
 * Printing itself is expected to run in 'unprinted' mode so already-printed
 * items are skipped.
 */
export function shouldAutoPrint({
  order,
  settings,
  deviceId,
}: {
  order: Pick<IOrder, 'status' | 'paymentStatus' | 'deviceId' | 'manual'>;
  settings: Pick<IPrintingSettings, 'trigger' | 'source'>;
  deviceId: string | null | undefined;
}): boolean {
  const { trigger, source } = settings;

  if (trigger === TicketPrintSetting.Manual) return false;

  const isLocal = order.deviceId === deviceId;

  // eslint-disable-next-line eqeqeq
  if (source == PrintSource.ThisDevice && !isLocal) return false;
  if (source === PrintSource.ThisDeviceAndOnline && !isLocal && order.manual) {
    return false;
  }

  switch (trigger) {
    case TicketPrintSetting.OnReceive:
      return true;
    case TicketPrintSetting.OnConfirm:
      return order.status === OrderStatus.Progress;
    case TicketPrintSetting.OnFinish:
      return order.status === OrderStatus.Finished;
    case TicketPrintSetting.OnPaid:
      return (
        order.status !== OrderStatus.Received &&
        order.paymentStatus === OrderPaymentStatus.Paid
      );
    default:
      return false;
  }
}

/** Ticket auto-print mode. OnPaid is order-level: print on payment regardless
 *  of pre-payment prints, gated only by the printed-while-paid marker.
 *  Other triggers keep per-item counter semantics. */
export function resolveTicketAutoPrintMode({
  settings,
  order,
}: {
  settings: Pick<IPrintingSettings, 'trigger'>;
  order: Pick<IOrder, 'printedPaidAt'>;
}): 'unprinted' | 'all' | 'skip' {
  if (settings.trigger !== TicketPrintSetting.OnPaid) return 'unprinted';
  return order.printedPaidAt ? 'skip' : 'all';
}

export type IGroupTicketMemberClassification =
  | { role: 'exclude' | 'context'; mode?: undefined }
  /** `mode` is only carried by receipt members. */
  | { role: 'receipt'; mode: 'all' | 'unprinted' };

/**
 * Classifies a group-document member. `exclude` members (canceled/declined)
 * are dropped from the document and its totals; `context` members are
 * rendered for correct selection totals but never marked; `receipt` members
 * (Paid + unstamped + gates pass) trigger the print and are the only ones
 * marked/stamped.
 *
 * Emergent "promotion": a store-resolved member that is Paid-but-unstamped
 * (e.g. paid earlier while the printer was offline) classifies as receipt
 * even if this payment did not touch it — it rides this document and gets
 * stamped, suppressing a stray later per-order catch-up print. Intended.
 */
export function classifyGroupTicketMember({
  order,
  settings,
  deviceId,
}: {
  order: Pick<
    IOrder,
    'status' | 'paymentStatus' | 'deviceId' | 'manual' | 'printedPaidAt'
  >;
  settings: Pick<IPrintingSettings, 'trigger' | 'source'>;
  deviceId: string | null | undefined;
}): IGroupTicketMemberClassification {
  if (!isOrderAutoPrintable(order)) return { role: 'exclude' }; // canceled/declined: not rendered, not counted
  if (!shouldAutoPrint({ order, settings, deviceId })) {
    return { role: 'context' }; // not Paid yet / Received / source-gated
  }
  const mode = resolveTicketAutoPrintMode({ settings, order });
  if (mode === 'skip') return { role: 'context' }; // printedPaidAt set: already receipted
  // The 'unprinted' outcome (non-OnPaid trigger) is only reachable through
  // the "trigger changed away from OnPaid during the debounce window"
  // degradation — kept as defensive coverage.
  return { role: 'receipt', mode }; // Paid + unstamped + gates pass
}

/**
 * Whether a ticket auto-print should be handed to the group-payment
 * aggregator (one combined ticket) instead of printing per order. Only the
 * OnPaid trigger groups: the transient groupPayment marker exists exactly
 * because the orders were paid together.
 */
export function shouldDeferToPaymentGroup({
  settings,
  groupPayment,
}: {
  settings: Pick<IPrintingSettings, 'trigger'>;
  groupPayment: IGroupPaymentInfo | undefined;
}): boolean {
  return (
    settings.trigger === TicketPrintSetting.OnPaid &&
    !!groupPayment &&
    groupPayment.orderIds.length > 1
  );
}
